/**
 * deadAirNudgeCooldown — how long a dismissed dead-air nudge stays quiet, and
 * what breaks the silence.
 *
 * Lives here rather than in the meeting page because it is a policy with two
 * specific wrong answers to avoid, and both are cheap to test and expensive to
 * find in a browser. The page owns one of these and asks it; nothing else in
 * the dead-air stack reads it.
 */

/**
 * The state a dismissal leaves behind: when the user closed the dialog, and
 * which peers it was about.
 *
 * A purely time-based cooldown fails in both directions at once. The user gets
 * nagged about a fault they already declined, because closing the dialog tells
 * the detector nothing and the next episode arrives a wait duration later; and
 * the user is silenced when a different peer breaks for a different reason,
 * because the timer cannot tell one fault from another.
 */
/**
 * Why a nudge that was otherwise due did not happen, for the suppression event.
 *
 * Only the two suppressions worth a row. The other three places a nudge does
 * not happen are already visible: a hidden page or a reconnecting room show up
 * as `pageHidden` and `connectionState` on the scan row, the first episode of a
 * run had nothing due yet, and the wait-duration debounce fires on nearly every
 * episode.
 */
export type NudgeSuppressionReason = 'dismissal_cooldown' | 'dialog_open'

export class DeadAirNudgeCooldown {
  private dismissedAt: number | null = null
  private dismissedPeers = new Set<string>()
  /** Reasons already reported in their current window. */
  private readonly reported = new Set<NudgeSuppressionReason>()

  /** The user closed a dialog that stood for these peers. */
  dismiss(now: number, identities: readonly string[]): void {
    this.dismissedAt = now
    this.dismissedPeers = new Set(identities)
    this.reported.delete('dismissal_cooldown')
  }

  /**
   * Forget the dismissal. The incident it belonged to is over — a recovery, a
   * dialog being shown, or the dialog being turned off — so whatever breaks
   * next is a new thing to tell the user about rather than a continuation they
   * already waved off.
   *
   * A room rebuilt by the ladder's reconnect needs no clear of its own. The
   * show site calls this on every dialog it puts up, so a dismissal never
   * outlives the next dialog, and the only dismissal that can still be standing
   * when a reconnect finishes is one the user made while watching that
   * reconnect — which is theirs to keep.
   */
  clear(): void {
    this.dismissedAt = null
    this.dismissedPeers.clear()
    this.reported.delete('dismissal_cooldown')
  }

  /**
   * A dialog actually opened, which starts a fresh window for the suppressions
   * it will cause while it is up.
   *
   * Not folded into {@link clear}, which the show site calls on its way past: a
   * `dialog_open` window that reset there would report every episode the open
   * dialog suppresses instead of the first.
   */
  shown(): void {
    this.reported.delete('dialog_open')
  }

  /**
   * Whether this suppression is the first of its kind in the current window,
   * and so worth a row.
   *
   * A client deaf for twenty minutes produces an episode every 30 seconds, and
   * one row per suppressed episode is forty rows saying the same thing. How
   * long the suppression lasted is recoverable from the dismissal plus the scan
   * rows that keep arriving.
   */
  shouldReport(reason: NudgeSuppressionReason): boolean {
    if (this.reported.has(reason)) return false
    this.reported.add(reason)
    return true
  }

  /**
   * How much silence is left for this escalation, or null when it is not
   * silenced at all.
   *
   * A peer the dismissal did not cover bypasses the timer. The bypass is on the
   * peer set alone, deliberately, and not on the failure types: a peer moves
   * between forks in the ordinary course of one continuous fault —
   * `classifyPublication` reaches `not_attached` only through a track that was
   * `not_subscribed` a moment earlier — so bypassing on a changed condition
   * would nudge twice for the single fault the cooldown exists to stop nudging
   * about.
   *
   * A shrinking set is still the same incident, so it stays silenced. Only a
   * peer that was not in the dismissed set is new information.
   */
  remainingSilence(
    now: number,
    identities: readonly string[],
    redisplayDelayMs: number
  ): number | null {
    if (this.dismissedAt === null) return null
    const remaining = redisplayDelayMs - (now - this.dismissedAt)
    if (remaining <= 0) return null
    if (identities.some((id) => !this.dismissedPeers.has(id))) return null
    return remaining
  }
}
